import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import EditorLayout from "../../../../components/Layouts/EditorLayout";
import SMCTextInput from "../../../../components/Simple/SMCTextInput";
import { SystemSettingsGetSchema } from "../../../../api/DBSchema/SystemSettings";
import SMCIcon from "../../../../components/Simple/SMCIcon";
import SMCLabel from "../../../../components/Simple/SMCLabel";
import SMCNumberInput from "../../../../components/Simple/SMCNumberInput";
import Sepperator from "../../../../components/Simple/Sepperator";
import SMCButton from "../../../../components/Simple/SMCButton";
import SMCLink from "../../../../components/Simple/SMCLink";
import Api from "../../../../api/SMPFetchHelper";
import { ObjectTypes } from "../../../../api/consts";
import { useSMCErrorHandler } from "../../../../utils/errorUtils";
import GenerateSystemReportSnackbarContent from "../../../PopupPages/GenerateSystemReportSnackbarContent";
import { useSnackbar } from "notistack";
import SMCField from "../../../../components/Simple/SMCField";
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';

const pageSchema = SystemSettingsGetSchema.pages.general;
const pageId = pageSchema.pageId;

function SystemSettingsGeneral({
  systemSettingsObject,
  setSystemSettingsData,
  setEditorPageId,
}) {
  const { t } = useTranslation();
  const { getErrorMessage } = useSMCErrorHandler();
  const { enqueueSnackbar } = useSnackbar();

  const pageData = systemSettingsObject[pageId];
  const setPageValue = useCallback(
    (paramName, value) => {
      setSystemSettingsData([pageId, paramName], value);
    },
    [setSystemSettingsData]
  );

  const dnsName = pageData[pageSchema.dnsName];
  const reportsServerAddress = pageData[pageSchema.reportsServerAddress];
  const cloudGenerateKeyUrl = pageData[pageSchema.cloudGenerateKeyUrl];
  const cloudGenerateBackupUploadUrlUrl = pageData[pageSchema.cloudGenerateBackupUploadUrlUrl];

  const cloudStatsServiceUrl =  pageData[pageSchema.cloudStatsServiceUrl];
  const cloudEventServiceUrl =  pageData[pageSchema.cloudEventServiceUrl];
  const cloudGatewayProxyServiceUrl = pageData[pageSchema.cloudGatewayProxyServiceUrl];
  const regKeyLength = pageData[pageSchema.regKeyLength];
  const heartbeatInterval = pageData[pageSchema.heartbeatInterval];
  const ddnsSuffix = pageData[pageSchema.ddnsSuffix];
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const [isUpdatingAppi, setIsUpdatingAppi] = useState(false);
  const [appiErrorMessage, setAppiErrorMessage] = useState(null);
  const [appiUpToDate, setaAppiUpToDate] = useState(false);

  const defaultServerGroupName = pageData[pageSchema.defaultServerGroupName];
  const availableServerGroups = pageData[pageSchema.availableServerGroups];
  

  const startReport = useCallback(() => {
    setIsGeneratingReport(true);  
    enqueueSnackbar(null, {
      persist: true,
      content: (key) => (
        <GenerateSystemReportSnackbarContent
          isGlobal={true}
          id={key}
          onClose={() => setIsGeneratingReport(false)}
        />
      ),
    });
  }, [enqueueSnackbar]);

  const updateAppi = useCallback(() => {
    setAppiErrorMessage(null);
    setIsUpdatingAppi(true);
    Api.doAction(ObjectTypes.SYSTEM_SETTINGS, "update-appi-now")
      .then(() => {
        setaAppiUpToDate(true);
      })
      .catch((e) => {
        const errorMessage = getErrorMessage(e);
        setAppiErrorMessage(errorMessage);
      })
      .finally(() => {
        setIsUpdatingAppi(false);
      });
  }, [getErrorMessage]);

  return (
    <EditorLayout>
      <SMCTextInput
        value={dnsName}
        onChange={(e) => setPageValue(pageSchema.dnsName, e.target.value)}
        label={t("SMP.SETTINGS.GENERAL.SMP_SYS_DNS_NAME")}
        tooltip={t("SMP.SETTINGS.GENERAL.SMP_SYS_DNS_NAME.TOOLTIP")}
      />

      <SMCTextInput
        value={reportsServerAddress}
        onChange={(e) =>
          setPageValue(pageSchema.reportsServerAddress, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.REPORTS_SERVER_ADDRESS")}
      />

      <SMCTextInput
        value={cloudGenerateKeyUrl}
        onChange={(e) =>
          setPageValue(pageSchema.cloudGenerateKeyUrl, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.CLOUD_GENERATE_KEY_URL")}
      />

      <SMCTextInput
        value={cloudGenerateBackupUploadUrlUrl}
        onChange={(e) =>
          setPageValue(pageSchema.cloudGenerateBackupUploadUrlUrl, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.CLOUD_GENERATE_BACKUP_UPLOAD_URL_URL")}
      />

      <SMCTextInput
        value={cloudEventServiceUrl}
        onChange={(e) =>
          setPageValue(pageSchema.cloudEventServiceUrl, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.CLOUD_EVENT_SERVICE_URL")}
      />

      <SMCTextInput
        value={cloudStatsServiceUrl}
        onChange={(e) =>
          setPageValue(pageSchema.cloudStatsServiceUrl, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.CLOUD_STATS_SERVICE_URL")}
      />

      <SMCTextInput
        value={cloudGatewayProxyServiceUrl}
        onChange={(e) =>
          setPageValue(pageSchema.cloudGatewayProxyServiceUrl, e.target.value)
        }
        label={t("SMP.SETTINGS.GENERAL.CLOUD_GATEWAY_PROXY_SERVICE_URL")}
      />


      <SMCField>
        <SMCTextInput
          value={ddnsSuffix}
          regexoEx={
            "^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$"
          }
          validationMsg={"Please enter a valid domain prefix"}
          onChange={(e) => setPageValue(pageSchema.ddnsSuffix, e.target.value)}
          label={t("SMP.SETTINGS.DNS.DOMAIN")}
          tooltip={t("SMP.SETTINGS.DNS.DOMAIN.TOOLTIP")}
        />
      </SMCField>

      <SMCNumberInput
        value={regKeyLength}
        onChange={(e) =>
          setPageValue(pageSchema.regKeyLength, parseInt(e.target.value))
        }
        label={t("SMP.SETTINGS.GENERAL.DEFAULT_REG_KEY_LENGTH")}
      />
      <SMCNumberInput
        value={heartbeatInterval}
        onChange={(e) =>
          setPageValue(pageSchema.heartbeatInterval, parseInt(e.target.value))
        }
        label={t("SMP.SETTINGS.GENERAL.GW_KEEP_ALIVE_INT")}
      />
      <SMCSelect
                label={t('SMP.SYSTEM.SERVICE_DOMAINS.GENERAL.DEFAULT_SERVER_GROUP')}
                value={defaultServerGroupName || ""}
                onChange={e => setPageValue(pageSchema.defaultServerGroupName, e.target.value === '' ? null : e.target.value)}
            >
              <SMCOption value= "" />
                {
                        availableServerGroups && availableServerGroups.map(defaultServerGroupName => (
                            <SMCOption key={defaultServerGroupName} value={defaultServerGroupName}>{defaultServerGroupName}</SMCOption>
                        ))
                }
            </SMCSelect>
      <SMCLabel>
        <SMCIcon icon="info" /> {t("SMP.SETTINGS.GENERAL.LINE1")}
      </SMCLabel>
      <Sepperator />
      <SMCButton disabled={isGeneratingReport} onClick={startReport}>
        {t("SMP.SETTINGS.GENERAL.GENERATE_REPORT")}
      </SMCButton>
      <SMCLink
        underline
        onClick={() =>
          setEditorPageId(
            SystemSettingsGetSchema.pages.notificationReports.pageId
          )
        }
      >
        {t("SMP.SETTINGS.GENERAL.CONF_PERIODIC_REPORTS")}
      </SMCLink>
      <Sepperator />
      <SMCButton disabled={isUpdatingAppi} onClick={updateAppi}>
        {t("SMP.SETTINGS.GENERAL.UPDATE_APPI")}
      </SMCButton>
      {isUpdatingAppi && (
        <SMCLabel style={{ color: "green" }}>APPI update is starting</SMCLabel>
      )}
      {appiErrorMessage && (
        <SMCLabel style={{ color: "red" }}>
          Error updating APPI
          <br />
          {appiErrorMessage}
        </SMCLabel>
      )}
      {!isUpdatingAppi && !appiErrorMessage && appiUpToDate && (
        <SMCLabel style={{ color: "green" }}>APPI up to date</SMCLabel>
      )}
    </EditorLayout>
  );
}

export default SystemSettingsGeneral;
